import { useAuthStore } from '../stores/authStore';

const API_PATH = '/atlrestapi/v2/';

/**
 * Default = same origin AND same sub-path as the page. Production hosts the
 * static SPA behind a web server (IIS/nginx/Caddy) with a reverse-proxy rule
 * that lands `<base>/atlrestapi/*` on the paired image service, so the
 * client never needs to know the service's port.
 *
 * Sub-path derivation: if the page is at `http://server/viewer/#/worklist`,
 * we compute `http://server/viewer` as the API base — so requests fire at
 * `http://server/viewer/atlrestapi/v2/` and hit the viewer sub-app's
 * web.config rewrite rule. Same pattern used by getDocxServiceUrl below.
 *
 * For Vite dev (`npm run dev`), pathname is `/` and origin is
 * `http://localhost:3000` — server.proxy handles the rewrite. The
 * `edgeweb-server` localStorage override still works for pointing at a
 * remote backend while debugging.
 */
export function getServerUrl(): string {
  const override = localStorage.getItem('edgeweb-server');
  if (override) return override;
  const origin = window.location.origin;
  // Strip trailing slash and any filename (e.g. `.../index.html`) from the
  // pathname to get the deployment folder — that's our API base.
  const base = window.location.pathname
    .replace(/\/+$/, '')
    .replace(/\/[^/]*\.html?$/i, '');
  return `${origin}${base}`;
}

export function setServerUrl(url: string) {
  localStorage.setItem('edgeweb-server', url);
}

/**
 * Syncfusion DocumentEditor server base URL — used for docx/html/rtf paste,
 * docx import, spell-check, etc. Runs as an IIS sub-application adjacent to
 * the main app. Override via localStorage when testing a different service.
 */
export function getDocxServiceUrl(): string {
  const override = localStorage.getItem('edgeweb-docx-service');
  if (override) return override;
  const origin = window.location.origin;
  // Derive from the current hash-routed path: e.g. /edgeweb4/#/report/1 → /edgeweb4
  const base = window.location.pathname.replace(/\/+$/, '').replace(/\/[^/]*\.html?$/i, '');
  return `${origin}${base}/dcx-service/api/documenteditor/`;
}

function getApiBase(): string {
  const server = getServerUrl();
  return `${server.replace(/\/$/, '')}${API_PATH}`;
}

/**
 * Test if the server is reachable by calling the echo endpoint.
 * Returns true if the server responds, false otherwise.
 */
export async function testServerConnection(serverUrl?: string): Promise<boolean> {
  const base = serverUrl
    ? `${serverUrl.replace(/\/$/, '')}${API_PATH}`
    : getApiBase();

  try {
    const response = await fetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cmd: 'echo' }),
      signal: AbortSignal.timeout(3000),
    });
    if (!response.ok) return false;
    const data = await response.json();
    return data.s === 'ok';
  } catch {
    return false;
  }
}

export interface ApiResponse<T = any> {
  s: 'ok' | 'error';
  error?: string;
  data?: T[];
}

function friendlyHttpError(): string {
  return 'Unable to reach the server. Please try again.';
}

/**
 * Parse the server response into an ApiResponse<T>, preferring the JSON body's
 * `error` field over a generic HTTP message. The Delphi backend returns a
 * proper `{s:"error", error:"..."}` payload for 4xx status codes too — a bare
 * check on response.ok would swallow those useful messages and surface
 * "Unable to reach the server" instead.
 */
async function parseApiResponse<T>(response: Response, cmd?: string): Promise<ApiResponse<T>> {
  // Try to read the JSON body regardless of HTTP status. If it's a legit
  // `{s, error?, data?}` shape, honour it — the backend authored that string.
  try {
    const body = await response.json();
    if (body && typeof body === 'object' && (body.s === 'ok' || body.s === 'error')) {
      return body as ApiResponse<T>;
    }
    // Body wasn't in our envelope shape. If HTTP was 2xx, treat as opaque ok.
    if (response.ok) return { s: 'ok' } as ApiResponse<T>;
  } catch (err) {
    // JSON parse or body read failed. Log it - silent catch hid a real
    // problem for weeks when a 6 MB reportHeader response tripped up
    // response.json() and every consumer saw an empty settings object.
    console.error('[api] parseApiResponse: JSON read/parse failed', {
      cmd, status: response.status, ok: response.ok,
      err: (err as any)?.message || String(err),
    });
  }
  return { s: 'error', error: friendlyHttpError() };
}

export async function apiCall<T = any>(
  cmd: string,
  params: Record<string, any> = {}
): Promise<ApiResponse<T>> {
  const acctoken = useAuthStore.getState().acctoken;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (acctoken) headers['Authorization'] = `Bearer ${acctoken}`;

  // Pre-login commands (login/echo) can run without a token; everything else
  // requires one and would crash the Pascal token validator on a null cast.
  // Fail fast on the client rather than firing a guaranteed-broken request.
  const PRE_AUTH_CMDS = new Set(['login', 'echo']);
  if (!acctoken && !PRE_AUTH_CMDS.has(cmd)) {
    return { s: 'error', error: 'Not authenticated. Please log in again.' };
  }

  // Strip null/undefined params so the JSON sent to the server never carries
  // `"foo": null` (some Pascal handlers cast-fail on JSON nulls when reading
  // string properties). Empty strings are fine and pass through.
  const cleanParams: Record<string, any> = {};
  for (const k of Object.keys(params)) {
    if (params[k] !== null && params[k] !== undefined) cleanParams[k] = params[k];
  }

  let response: Response;
  try {
    response = await fetch(getApiBase(), {
      method: 'POST',
      headers,
      // Keep acctoken in the body too for back-compat until the dispatcher is
      // updated to consume the Authorization header. Backend reads either.
      body: JSON.stringify({ acctoken, cmd, ...cleanParams }),
    });
  } catch {
    // Network-level failure — DNS, connection refused, CORS, offline, etc.
    return { s: 'error', error: friendlyHttpError() };
  }

  return parseApiResponse<T>(response, cmd);
}

export async function loginCall(
  username: string,
  password: string
): Promise<ApiResponse<{ acctoken: string; displayName: string; userId: number }>> {
  let response: Response;
  try {
    response = await fetch(getApiBase(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cmd: 'login', username, password }),
    });
  } catch {
    return { s: 'error', error: friendlyHttpError() };
  }

  return parseApiResponse<{ acctoken: string; displayName: string; userId: number }>(response);
}
